/**
 * Parser for WebSearch tool responses.
 *
 * The WebSearch backend tool (benchling/llm/tools/web_search_tool.py) returns
 * the raw text content from an AgentCore Gateway MCP call. In practice that
 * text is a JSON document shaped like:
 *
 *   {"id":"...","results":[{"title":"...","url":"...","text":"...","publishedDate":"..."}, ...]}
 *
 * We parse it into a typed list of results so the activity modal can render
 * clickable source cards instead of dumping the raw JSON blob.
 */

export interface WebSearchResult {
  title: string;
  url: string;
  text: string;
  /** Human-readable published date from the provider, or null when absent. */
  publishedDate: string | null;
}

function coerceString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function parseResult(value: unknown): WebSearchResult | null {
  if (value == null || typeof value !== 'object') {
    return null;
  }
  const obj = value as Record<string, unknown>;
  const title = coerceString(obj.title).trim();
  const url = coerceString(obj.url).trim();
  // A result with neither a title nor a URL isn't worth rendering as a card.
  if (title === '' && url === '') {
    return null;
  }
  const publishedDate = coerceString(obj.publishedDate).trim();
  return {
    title,
    url,
    text: coerceString(obj.text).trim(),
    publishedDate: publishedDate === '' ? null : publishedDate,
  };
}

/**
 * Parse a WebSearch tool response into a list of results.
 *
 * Returns null when the response isn't the expected JSON shape (e.g. an error
 * string or a "no results" message), or when it contains no renderable results
 * (empty array / all results unusable). In those cases, the caller should fall back
 * to rendering the raw response text.
 */
export function parseWebSearchResults(responseText: string): Array<WebSearchResult> | null {
  const trimmed = responseText.trim();
  if (trimmed === '') {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (error) {
    // JSON.parse throws SyntaxError for non-JSON responses.
    if (error instanceof SyntaxError) {
      return null;
    }
    throw error;
  }

  if (parsed == null || typeof parsed !== 'object') {
    return null;
  }

  const rawResults = (parsed as {results?: unknown}).results;
  if (!Array.isArray(rawResults)) {
    return null;
  }

  const results = rawResults
    .map((raw) => parseResult(raw))
    .filter((result): result is WebSearchResult => result != null);

  return results.length > 0 ? results : null;
}
